import { useState } from 'react';
import {
  Stack,
  Title,
  Text,
  TextInput,
  Button,
  Paper,
  Group,
  ThemeIcon,
  Alert,
  Loader,
  Radio,
  Pagination,
  Divider,
  SegmentedControl
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { 
  IconMapPin, 
  IconSearch, 
  IconCurrentLocation, 
  IconAlertCircle,
  IconCar,
  IconTruck
} from '@tabler/icons-react';
import { KrogerLocation } from '../../types/kroger';
import { isValidZipCode, searchKrogerLocations, searchKrogerLocationsByCoords } from '../../utils/krogerLocations';

type KrogerLocationPickerProps = {
  onLocationSelect: (location: KrogerLocation, fulfillmentPreference: 'pickup' | 'delivery') => void;
  selectedLocationId?: string;
  selectedFulfillmentPreference?: 'pickup' | 'delivery';
};

export const KrogerLocationPicker = ({ onLocationSelect, selectedLocationId, selectedFulfillmentPreference }: KrogerLocationPickerProps) => {
  const [zipCode, setZipCode] = useState('');
  const [locations, setLocations] = useState<KrogerLocation[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isUsingLocation, setIsUsingLocation] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [fulfillmentPreference, setFulfillmentPreference] = useState<'pickup' | 'delivery'>(selectedFulfillmentPreference || 'pickup');
  const itemsPerPage = 5;
  const isMobile = useMediaQuery('(max-width: 48em)');

  const handleZipCodeSearch = async () => {
    if (!isValidZipCode(zipCode)) {
      setError('Please enter a valid ZIP code');
      return;
    }

    setIsSearching(true);
    setError(null);
    setHasSearched(true);

    try {
      const results = await searchKrogerLocations(zipCode.trim());
      setLocations(results);
      setCurrentPage(1); // Reset to first page
      
      if (results.length === 0) {
        setError('No Kroger stores found in this area. Try a different ZIP code.');
      } else if (!selectedLocationId) {
        // Auto-select the first store
        setTimeout(() => {
          const firstRadio = document.querySelector('input[type="radio"]') as HTMLInputElement;
          if (firstRadio) {
            firstRadio.click();
          }
        }, 50);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search for stores');
      setLocations([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleLocationSearch = async () => {
    if (!navigator.geolocation) {
      setError('Location services are not available in your browser');
      return;
    }

    setIsUsingLocation(true);
    setError(null);
    setHasSearched(true);

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const results = await searchKrogerLocationsByCoords(latitude, longitude);
          setLocations(results);
          setCurrentPage(1); // Reset to first page
          
          if (results.length === 0) {
            setError('No Kroger stores found near your location.');
          } else if (!selectedLocationId) {
            // Auto-select the first store
            setTimeout(() => {
              const firstRadio = document.querySelector('input[type="radio"]') as HTMLInputElement;
              if (firstRadio) {
                firstRadio.click();
              }
            }, 50);
          }
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to search for stores');
          setLocations([]);
        } finally {
          setIsUsingLocation(false);
        }
      },
      (error) => {
        setIsUsingLocation(false);
        setError('Unable to access your location. Please use ZIP code search instead.');
        console.error('Geolocation error:', error);
      }
    );
  };

  const formatAddress = (location: KrogerLocation) => {
    const parts = [location.address.addressLine1];
    if (location.address.addressLine2) {
      parts.push(location.address.addressLine2);
    }
    parts.push(`${location.address.city}, ${location.address.state} ${location.address.zipCode}`);
    return parts.join(', ');
  };

  // Calculate pagination
  const totalPages = Math.ceil(locations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLocations = locations.slice(startIndex, endIndex);


  return (
    <Stack gap="lg">
      {/* Search Section */}
      <Stack gap="md">
        <Title order={3} size="h4" fw={600}>
          Find your Kroger store
        </Title>
        
        {/* ZIP Code Search */}
        <Group gap="sm" align="flex-end">
          <TextInput
            label="ZIP Code"
            placeholder="Enter your ZIP code"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleZipCodeSearch();
              }
            }}
          />
          <Button
            leftSection={<IconSearch size={16} />}
            onClick={handleZipCodeSearch}
            loading={isSearching}
            disabled={isSearching || isUsingLocation}
          >
            Search
          </Button>
        </Group>

        {/* Location Search */}
        <Group justify="center">
          <Text size="sm" c="dimmed">or</Text>
        </Group>
        
        <Group justify="center">
          <Button
            variant="light"
            leftSection={<IconCurrentLocation size={16} />}
            onClick={handleLocationSearch}
            disabled={isSearching || isUsingLocation}
          >
            Use my location
          </Button>
        </Group>
      </Stack>

      {/* Error Alert */}
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" radius="lg">
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {(isSearching || isUsingLocation) && (
        <Group justify="center" py="xl">
          <Loader size="md" />
          <Text c="dimmed">
            Searching for nearby stores...
          </Text>
        </Group>
      )}

      {/* Results */}
      {hasSearched && !isSearching && !isUsingLocation && locations.length > 0 && (
        <Stack gap="md">
          <Title order={4} size="h5" fw={600}>
            Choose your store ({locations.length} found)
          </Title>
          
          <Radio.Group
            value={selectedLocationId}
            onChange={(locationId) => onLocationSelect(locations.find((location) => location.locationId === locationId)!, fulfillmentPreference)}
          >
            <Stack gap="xs">
              {currentLocations.map((location) => (
                <Paper
                  key={location.locationId}
                  p="md"
                  radius="lg"
                  style={{
                    border: selectedLocationId === location.locationId 
                      ? '1px solid var(--mantine-color-orange-6)'
                      : '1px solid var(--mantine-color-gray-3)',
                    background: selectedLocationId === location.locationId ? 'var(--mantine-color-orange-0)' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                  onClick={() => {
                    // Trigger the radio selection by simulating a click on the radio input
                    const radioElement = document.querySelector(`input[value="${location.locationId}"]`) as HTMLInputElement;
                    if (radioElement) {
                      radioElement.click();
                    }
                  }}
                >
                  <Group gap="md" align="flex-start">
                    <Radio
                      value={location.locationId}
                      size="md"
                      color="orange"
                      styles={{
                        radio: {
                          cursor: 'pointer'
                        }
                      }}
                    />
                    
                    <Stack gap="xs" style={{ flex: 1 }}>
                      <Text fw={600} size="md">
                        {location.name}
                      </Text>
                      
                      <Text size="sm" c="dimmed">
                        {formatAddress(location)}
                      </Text>
                    </Stack>
                  </Group>
                </Paper>
              ))}
            </Stack>
          </Radio.Group>

          {/* Pagination */}
          {totalPages > 1 && (
            <Group justify="center" mt="lg">
              <Pagination
                total={totalPages}
                value={currentPage}
                onChange={setCurrentPage}
                size={isMobile ? 'sm' : 'md'}
                radius="md"
                color="orange"
                siblings={isMobile ? 0 : 1}
                boundaries={isMobile ? 0 : 1}
              />
            </Group>
          )}

          {/* Fulfillment Preference */}
          {locations.length > 0 && (
            <>
              <Divider />
              <Stack gap="md">
                <Title order={4} size="h5" fw={600}>
                  How would you like to get your groceries?
                </Title>
                
                {/* Mobile: Vertical */}
                <SegmentedControl
                  value={fulfillmentPreference}
                  onChange={(value) => {
                    setFulfillmentPreference(value as 'pickup' | 'delivery');
                    // If a location is selected, update with new preference
                    if (selectedLocationId) {
                      onLocationSelect(locations.find((location) => location.locationId === selectedLocationId)!, value as 'pickup' | 'delivery');
                    }
                  }}
                  data={[
                    {
                      label: (
                        <Group gap="sm">
                          <IconCar size={16} />
                          <Text size="sm">Pickup</Text>
                        </Group>
                      ),
                      value: 'pickup'
                    },
                    {
                      label: (
                        <Group gap="sm">
                          <IconTruck size={16} />
                          <Text size="sm">Delivery</Text>
                        </Group>
                      ),
                      value: 'delivery'
                    }
                  ]}
                  orientation="vertical"
                  size="md"
                  color="orange"
                  fullWidth
                  hiddenFrom="sm"
                  styles={{
                    control: {
                      padding: '8px 12px'
                    }
                  }}
                />
                
                {/* Desktop: Horizontal */}
                <SegmentedControl
                  value={fulfillmentPreference}
                  onChange={(value) => {
                    setFulfillmentPreference(value as 'pickup' | 'delivery');
                    // If a location is selected, update with new preference
                    if (selectedLocationId) {
                      onLocationSelect(locations.find((location) => location.locationId === selectedLocationId)!, value as 'pickup' | 'delivery');
                    }
                  }}
                  data={[
                    {
                      label: (
                        <Group gap="sm">
                          <IconCar size={16} />
                          <Text size="sm">Pickup</Text>
                        </Group>
                      ),
                      value: 'pickup'
                    },
                    {
                      label: (
                        <Group gap="sm">
                          <IconTruck size={16} />
                          <Text size="sm">Delivery</Text>
                        </Group>
                      ),
                      value: 'delivery'
                    }
                  ]}
                  orientation="horizontal"
                  size="md"
                  color="orange"
                  visibleFrom="sm"
                  styles={{
                    control: {
                      padding: '8px 12px'
                    }
                  }}
                />
              </Stack>
            </>
          )}
        </Stack>
      )}

      {/* No Results */}
      {hasSearched && !isSearching && !isUsingLocation && locations.length === 0 && !error && (
        <Paper p="xl" radius="lg" style={{ textAlign: 'center' }}>
          <Stack gap="md" align="center">
            <ThemeIcon size={60} radius="xl" color="gray" variant="light">
              <IconMapPin size={30} />
            </ThemeIcon>
            <Stack gap="xs" align="center">
              <Text fw={600} c="dimmed">
                No stores found
              </Text>
              <Text size="sm" c="dimmed">
                Try searching with a different ZIP code or location
              </Text>
            </Stack>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
};
